import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { GridRegistrationModel } from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import {
  ComparisonTableData,
  ComparisonTableHeader
} from '@bo-client/shared/components/comparison-table/comparison-table.component';
import {
  MastrLocationDto,
  MastrPvPlantWithLocationDto
} from '@bo-client/activation-management/shared/components/mastr-validation/MastrData.dto';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MastrErrorHint, mastrErrorCodeFromDetail, resolveMastrErrorHint } from './mastr-error-hint';
import {
  catchError,
  combineLatestWith,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  tap
} from 'rxjs/operators';
import { Observe } from '@bo-client/shared/observe.util';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { NBPConfig } from '@bo-client/shared/config.model';
import { FeederContractModel } from '@bo-client/activation-management/feeder-contract/model/feeder-contract.model';
import { RegexToCheckModuleMaStrNumber } from '@bo-client/shared/forms/forms.validators';
import { CompanyLocation } from '@bo-client/shared/models/companyLocations';
import {
  StorageCouplingTypeDisplayNames,
  StorageCouplingTypeEnum
} from '@bo-client/activation-management/grid-registration/model/storage.model';

/**
 * Eintrag in `mastrDataMismatchFields`, der dem Parent-Formular signalisiert,
 * dass die MaStR-Nummer selbst (noch) nicht gültig ist – z.B. weil sie leer ist,
 * dem Pflichtmuster nicht entspricht, gerade geladen wird oder die Daten nicht
 * abrufbar sind. Im Erfolgsfall taucht dieser Eintrag NICHT mehr im Output auf.
 */
export const MASTR_NUMBER_MISMATCH_TAG = 'mastrNumber';

/**
 * Lebenszyklus der MaStR-Nummer‐Validierung, den die Komponente an ihre Umgebung
 * (z.B. das Inbetriebsetzungsformular) kommuniziert. `'valid'` ist der einzige
 * Status, in dem das Parent-Formular eine Absendung allein anhand seiner
 * eigenen Validatoren und der Datenvergleichstabelle entscheiden darf.
 */
export type MastrNumberStatus = 'pattern-mismatch' | 'loading' | 'invalid' | 'valid';

@Component({
  selector: 'app-mastr-validation',
  templateUrl: './mastr-validation.component.html',
  styleUrl: './mastr-validation.component.scss'
})
export class MastrValidationComponent implements OnInit {
  /** Datensatz der Netzanmeldung */
  @Input() grData: GridRegistrationModel | FeederContractModel;
  @Observe('grData') grData$!: Observable<GridRegistrationModel | FeederContractModel>;
  /** Nummer im Marktstammdatenregister */
  @Input() mastrNumber: string;
  @Observe('mastrNumber') mastrNumber$: Observable<string>;

  @Input() companyLocationExternalId: CompanyLocation['external_id'];
  @Observe('companyLocationExternalId') companyLocationExternalId$: Observable<
    CompanyLocation['external_id']
  >;

  /** Use storage-unit MaStR API and (later) storage-specific comparison rows. */
  @Input() mastrFetchMode: 'producerPv' | 'storageUnit' = 'producerPv';
  @Observe('mastrFetchMode') mastrFetchMode$!: Observable<'producerPv' | 'storageUnit'>;

  @Output() mastrDataMismatchFields = new EventEmitter<string[]>();
  @Output() mastrDataFetch = new EventEmitter<MastrPvPlantWithLocationDto>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly httpHapService = inject(HttpHapService);
  private readonly dataShareService = inject(DataShareService);
  private readonly authorizationService = inject(AuthorizationService);

  /**
   * Ob der angemeldete Benutzer ein Installateur ist (sonst: interner Sachbearbeiter). Steuert den
   * rollenabhängigen Kontakt-Hinweis in Fehlermeldungen. Wird in {@link ngOnInit} aktualisiert.
   */
  private isInstaller = false;

  /**
   * Aktueller Status der MaStR-Nummer-Validierung. Das Parent-Formular nutzt
   * diesen Wert (indirekt via `mastrDataMismatchFields`) um die Abgabe zu blockieren,
   * solange noch keine validen Daten vorliegen.
   */
  private readonly _mastrNumberStatus$ = new BehaviorSubject<MastrNumberStatus>('pattern-mismatch');
  readonly mastrNumberStatus$ = this._mastrNumberStatus$.asObservable();

  /** Aktuell letzte vom Vergleich erkannte Datenfeld-Abweichungen (ohne Sentinel). */
  private lastDataMismatchFields: string[] = [];

  /** Funktionen, zum Mappen der Netzanmeldungsdaten,
   * um sie in das Format vom Marktstammdatenregister zu bekommen*/
  private readonly dataMapUtil = {
    gridRegistrationModel: {
      getGrModuleNumber(data: GridRegistrationModel | FeederContractModel) {
        return data.pvModules.map((item) => item.count).reduce((acc, cur) => acc + cur, 0);
      },
      getInstalledPower(data: GridRegistrationModel | FeederContractModel) {
        return data.producers
          .map((item) => item.count * item.acActivePowerMax)
          .reduce((acc, cur) => acc + cur, 0);
      },
      getBruttoPower(data: GridRegistrationModel | FeederContractModel) {
        return (
          data.pvModules.map((item) => item.count * item.modulePowerWatt).reduce((acc, cur) => acc + cur, 0) /
          1000
        );
      },
      getCustomerData: (key: keyof (GridRegistrationModel | FeederContractModel)['customer']) => {
        return (data: GridRegistrationModel | FeederContractModel) => (data.customer || {})[key];
      },
      getConnectionObjectData: (
        key: keyof (GridRegistrationModel | FeederContractModel)['connectionObject']
      ) => {
        return (data: GridRegistrationModel | FeederContractModel) => {
          return (data.connectionObject || {})[key];
        };
      }
    },
    mastrDataModel: {
      getConnectionObjectData: (key: keyof MastrLocationDto) => {
        return (data: MastrPvPlantWithLocationDto) => (data.location || {})[key];
      }
    }
  };

  /**
   * Vergleichszeilen für die Validierung einer **Stromspeichereinheit**.
   *
   * Im Speicher-Prozess steht nicht die Erzeugungsanlage im Vordergrund, sondern der Speicher:
   * PV-spezifische Zeilen (Modulanzahl, Modulleistung) entfallen deshalb vollständig. Die
   * MaStR-Schnittstelle liefert zur Speichereinheit nur Kapazität, Kopplung und Technologie —
   * Leistungswerte sind dort nicht hinterlegt und können daher nicht verglichen werden.
   */
  private readonly dataGettersStorage: DataGetter[] = [
    {
      label: 'Speicherkapazität (gesamt)',
      // Gleicher Formfeldname wie bei der PV-Validierung, damit dieselbe Toleranz greift
      formFieldName: 'storages[0].usableCapacity',
      grDataGetter: (data) => {
        const storage = data.storages?.[0];
        if (!storage || storage.usableCapacity == null || storage.count == null) {
          return null;
        }
        return Number(storage.usableCapacity) * Number(storage.count);
      },
      mastrDataGetter: (data) => data.location?.storageUnits?.[0]?.nutzbareSpeicherkapazitaet ?? null
    },
    {
      label: 'Art der Kopplung',
      formFieldName: 'storages[0].couplingType',
      grDataGetter: (data) => normalizeStorageCouplingType(data.storages?.[0]?.couplingType),
      mastrDataGetter: (data) => normalizeStorageCouplingType(data.location?.storageUnits?.[0]?.acDcKoppelung)
    },
    {
      label: 'PLZ Anschlussobjekt',
      formFieldName: 'customer.zipCode',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getConnectionObjectData('zipCode'),
      mastrDataGetter: this.dataMapUtil.mastrDataModel.getConnectionObjectData('postleitzahl'),
      warnOnly: true
    },
    {
      label: 'Ort Anschlussobjekt',
      formFieldName: 'customer.city',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getConnectionObjectData('city'),
      mastrDataGetter: this.dataMapUtil.mastrDataModel.getConnectionObjectData('ort'),
      warnOnly: true
    },
    {
      label: 'Straße Anschlussobjekt',
      formFieldName: 'customer.street',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getConnectionObjectData('street'),
      mastrDataGetter: this.dataMapUtil.mastrDataModel.getConnectionObjectData('strasse'),
      warnOnly: true
    },
    {
      label: 'HausNr Anschlussobjekt',
      formFieldName: 'customer.streetNo',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getConnectionObjectData('streetNo'),
      mastrDataGetter: this.dataMapUtil.mastrDataModel.getConnectionObjectData('hausnummerWert'),
      warnOnly: true
    }
  ];

  /** Gibt ein Schema für die Daten an, die in der Vergleichstabelle angezeigt werden sollen.
   *
   * Enthält das Label der Datensätze sowie die Getter-Funktionen oder Schlüssel,
   * um die Daten aus dem GridRegistrationModel und dem MastrPvPlantWithLocationDto zu extrahieren.
   */
  private readonly dataGetters: DataGetter[] = [
    {
      label: 'AC-maximale Wirkleistung (gesamt)',
      formFieldName: 'newProduction',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getInstalledPower,
      mastrDataGetter: (data) => data.pvUnit?.zugeordneteWirkleistungWechselrichter
    },
    {
      label: 'Anzahl der Module',
      formFieldName: 'pvModules',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getGrModuleNumber,
      mastrDataGetter: (data) => data.pvUnit?.anzahlModule
    },
    {
      label: 'Gesamtleistung der PV-Module',
      formFieldName: 'newInstalledPower',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getBruttoPower,
      mastrDataGetter: (data) => data.pvUnit?.installierteLeistung
    },
    {
      label: 'PLZ Anschlussobjekt',
      formFieldName: 'customer.zipCode',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getConnectionObjectData('zipCode'),
      mastrDataGetter: this.dataMapUtil.mastrDataModel.getConnectionObjectData('postleitzahl'),
      warnOnly: true
    },
    {
      label: 'Ort Anschlussobjekt',
      formFieldName: 'customer.city',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getConnectionObjectData('city'),
      mastrDataGetter: this.dataMapUtil.mastrDataModel.getConnectionObjectData('ort'),
      warnOnly: true
    },
    {
      label: 'Straße Anschlussobjekt',
      formFieldName: 'customer.street',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getConnectionObjectData('street'),
      mastrDataGetter: this.dataMapUtil.mastrDataModel.getConnectionObjectData('strasse'),
      warnOnly: true
    },
    {
      label: 'HausNr Anschlussobjekt',
      formFieldName: 'customer.streetNo',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getConnectionObjectData('streetNo'),
      mastrDataGetter: this.dataMapUtil.mastrDataModel.getConnectionObjectData('hausnummerWert'),
      warnOnly: true
    },
    {
      label: 'Speicherkapazität',
      formFieldName: 'storages[0].usableCapacity',
      grDataGetter: (data) =>
        (!!data.storages.length && (data.storages[0].usableCapacity || 0) * (data.storages[0].count || 0)) ||
        '',
      mastrDataGetter: (data) =>
        (!!data.location?.storageUnits?.length && data.location.storageUnits[0].nutzbareSpeicherkapazitaet) ||
        '',
      show: (model, pvPlant) => !!pvPlant.location.storageUnits.length
    }
  ];
  readonly tableHeader: ComparisonTableHeader = {
    labelHeader: 'Beschreibung',
    firstDataHeader: 'Angabe im Portal',
    secondDataHeader: 'Angabe im Marktstammdatenregister'
  };
  _data$: BehaviorSubject<ComparisonTableData[]> = new BehaviorSubject([]);
  data$ = this._data$.asObservable();

  private _error$ = new BehaviorSubject<MastrErrorHint | null>(null);
  error$ = this._error$.asObservable();

  private emptyComparisonData(): ComparisonTableData[] {
    return [
      {
        label: 'Keine Daten verfügbar',
        firstDataPoint: '—',
        secondDataPoint: '—'
      }
    ];
  }

  private mapToComparisonData(
    pvPlant: MastrPvPlantWithLocationDto,
    model: GridRegistrationModel | FeederContractModel,
    config: NBPConfig,
    fetchMode: 'producerPv' | 'storageUnit' = 'producerPv'
  ): ComparisonTableData[] {
    const getters = fetchMode === 'storageUnit' ? this.dataGettersStorage : this.dataGetters;
    return getters.map((getter) => {
      let allowedDeviation: number;
      switch (getter.formFieldName) {
        case 'newInstalledPower': {
          const deviation = (
            config.gridRegistration?.mastrValidationConfig?.allowedPowerDeviation || '0'
          ).replace(',', '.');
          allowedDeviation = Number(deviation);
          break;
        }
        case 'newProduction': {
          const deviation = (
            config.gridRegistration?.mastrValidationConfig?.allowedPowerDeviation || '0'
          ).replace(',', '.');
          allowedDeviation = Number(deviation);
          break;
        }
        case 'storages[0].usableCapacity': {
          const deviation = (
            config.gridRegistration?.mastrValidationConfig?.allowedCapacityDeviation || '0'
          ).replace(',', '.');
          allowedDeviation = Number(deviation);
          break;
        }
        case 'pvModules': {
          const deviation =
            // Regex prüft auf Komma und Punkt als Dezimaltrennzeichen
            config.gridRegistration?.mastrValidationConfig?.allowedModuleCountDeviation.split(/[,.]/)[0];
          allowedDeviation = Number(deviation);
          break;
        }
        default: {
          break;
        }
      }
      const strictAddressValidation =
        config.gridRegistration?.mastrValidationConfig?.enableStrictAddressValidation || false;
      return {
        label: getter.label,
        warnOnly:
          getter.formFieldName?.startsWith('customer.') && strictAddressValidation ? false : getter.warnOnly,
        allowedDeviation: allowedDeviation || undefined,
        firstDataPoint:
          typeof getter.grDataGetter === 'function'
            ? getter.grDataGetter.call(this, model)
            : model[getter.grDataGetter],
        secondDataPoint:
          typeof getter.mastrDataGetter === 'function'
            ? getter.mastrDataGetter.call(this, pvPlant)
            : pvPlant[getter.mastrDataGetter]
      };
    });
  }

  ngOnInit(): void {
    this.authorizationService.isInstaller$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isInstaller) => (this.isInstaller = isInstaller));
    this.mapData();
  }

  /**
   * Übersetzt einen im Datenabruf aufgetretenen Fehler in einen anzuzeigenden Hinweis. Bei einer
   * {@link HttpErrorResponse} wird der Maschinen-Code aus dem `detail`-Feld (ersatzweise der
   * HTTP-Status) ausgewertet; andere Fehler ergeben einen generischen Hinweis.
   */
  private toMastrError(err: unknown): MastrErrorHint {
    if (err instanceof HttpErrorResponse) {
      const code = mastrErrorCodeFromDetail(err.error?.detail);
      return resolveMastrErrorHint(code, err.status, this.isInstaller);
    }
    return resolveMastrErrorHint(null, null, this.isInstaller);
  }

  private mapData() {
    this.mastrNumber$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        combineLatestWith(this.companyLocationExternalId$, this.mastrFetchMode$),
        distinctUntilChanged((previous, current) => {
          return previous[0] === current[0] && previous[1] === current[1] && previous[2] === current[2];
        }),
        tap(() => {
          // Beim Wechsel der Eingabe alte Vergleichsdaten leeren und den Parent-Status zurücksetzen.
          this.lastDataMismatchFields = [];
          this._data$.next([]);
          this._error$.next(null);
        }),
        filter(([mastrNumber, , mode]) => this.checkValidity(mastrNumber, mode ?? 'producerPv')),
        tap(() => {
          // Pattern hat gepasst → Daten werden geladen, Formular muss währenddessen blockiert bleiben.
          this.setMastrNumberStatus('loading');
        }),
        switchMap(([mastrNr, companyExternalId, mode]) => {
          const fetchMode = mode ?? 'producerPv';
          const basePath =
            fetchMode === 'storageUnit'
              ? `/external/mastr-data-storage/${mastrNr}`
              : `/external/mastr-data/${mastrNr}`;
          const url = companyExternalId ? `${basePath}?externalId=${companyExternalId}` : basePath;
          return this.httpHapService.getTyped<MastrPvPlantWithLocationDto>(url).pipe(
            tap((response) => {
              this.mastrDataFetch.emit(response);
              this.setMastrNumberStatus('valid');
            }),
            combineLatestWith(this.grData$, this.dataShareService.tenantConfig$),
            map(([pvPlant, grData, config]) => this.mapToComparisonData(pvPlant, grData, config, fetchMode)),
            catchError((err) => {
              // eslint-disable-next-line no-console
              console.error('Fehler beim Mastr‑Abruf', err);
              this._error$.next(this.toMastrError(err));
              this.setMastrNumberStatus('invalid');
              return of(this.emptyComparisonData()); // Stream bleibt lebendig
            })
          );
        }),
        // **alle übrigen** Fehler abfangen (z. Tippfehler in mapToComparisonData)
        catchError((err) => {
          // eslint-disable-next-line no-console
          console.error('Unerwarteter Fehler in der Pipeline', err);
          this._error$.next(this.toMastrError(err));
          this.setMastrNumberStatus('invalid');
          return of(this.emptyComparisonData());
        }),
        shareReplay(1)
      )
      .subscribe((data) => {
        this._data$.next(data);
      });
  }

  /**
   * Aktualisiert den Status der MaStR-Nummer-Prüfung und sendet den passenden
   * `mastrDataMismatchFields`-Output an die Parent-Komponente. Solange der Status
   * nicht `'valid'` ist, wird ein Sentinel-Eintrag {@link MASTR_NUMBER_MISMATCH_TAG}
   * vorne angefügt, damit das Formular die Abgabe blockiert.
   */
  private setMastrNumberStatus(status: MastrNumberStatus): void {
    this._mastrNumberStatus$.next(status);
    this.emitMismatchFields();
  }

  private emitMismatchFields(): void {
    const status = this._mastrNumberStatus$.getValue();
    if (status === 'valid') {
      this.mastrDataMismatchFields.emit([...this.lastDataMismatchFields]);
    } else {
      this.mastrDataMismatchFields.emit([MASTR_NUMBER_MISMATCH_TAG]);
    }
  }

  handleFieldMismatch(labels: string[]): void {
    // Beide Zeilensätze durchsuchen: Im Speicher-Modus stammen die Labels aus `dataGettersStorage`.
    const allGetters = [...this.dataGetters, ...this.dataGettersStorage];
    this.lastDataMismatchFields = labels.map((label) => {
      const getter = allGetters.find((g) => g.label === label);
      if (!getter) {
        // eslint-disable-next-line no-console
        console.warn(`Kein Getter für Label "${label}" gefunden.`);
        return label; // Fallback auf das Label selbst
      }
      if (getter.formFieldName) return getter.formFieldName;
      if (typeof getter.grDataGetter === 'string') return getter.grDataGetter;
      return label;
    });
    this.emitMismatchFields();
  }

  checkValidity = (mastrNumber: string, mode: 'producerPv' | 'storageUnit' = 'producerPv'): boolean => {
    if (!mastrNumber?.length) {
      this._error$.next({
        title: 'Keine Marktstammdatenregister-Nummer angegeben',
        message:
          'Bitte geben Sie eine gültige Marktstammdatenregister-Nummer ein, um eine Datenvalidierung zu erhalten'
      });
      this.setMastrNumberStatus('pattern-mismatch');
      return false;
    }
    // Stromspeichereinheiten nutzen dasselbe SEE-Nummernformat wie Erzeugungseinheiten
    if (!RegexToCheckModuleMaStrNumber.test(mastrNumber)) {
      this._error$.next({
        title: 'Ungültige Marktstammdatenregister-Nummer',
        message:
          mode === 'storageUnit'
            ? 'Die eingegebene Marktstammdatenregister-Nummer des Speichers entspricht keinem gültigen Format. Bitte überprüfen Sie die Eingabe. (Beginnend mit "SEE9", gefolgt von 11 weiteren Ziffern)'
            : 'Die eingegebene Marktstammdatenregister-Nummer entspricht keinem gültigen Format. Bitte überprüfen Sie die Eingabe. (Beginnend mit "SEE9", gefolgt von 11 weiteren Ziffern)'
      });
      this.setMastrNumberStatus('pattern-mismatch');
      return false;
    }
    return true;
  };
}

/**
 * Vereinheitlicht die Systemkopplung für den Vergleich.
 *
 * Das Portal speichert `AC`/`DC`, das MaStR liefert den Namen des Enum-Werts
 * (`A_CGEKOPPELTES_SYSTEM` / `D_CGEKOPPELTES_SYSTEM` / `NONE`).
 *
 * @returns Anzeigetext der Kopplungsart oder `null`, wenn der Wert fehlt oder unbekannt ist —
 *   `null` lässt die Vergleichszeile durch `ignoreIfSecondIsNull` unbewertet.
 */
function normalizeStorageCouplingType(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const normalized = value.replace(/_/g, '').toUpperCase();
  if (normalized.startsWith('AC')) {
    return StorageCouplingTypeDisplayNames[StorageCouplingTypeEnum.AC];
  }
  if (normalized.startsWith('DC')) {
    return StorageCouplingTypeDisplayNames[StorageCouplingTypeEnum.DC];
  }
  return null;
}

type DataGetter =
  | {
      label: string;
      formFieldName?: string; // Optional, falls der Getter nicht dem Formfeldnamen entspricht
      grDataGetter: keyof (GridRegistrationModel | FeederContractModel);
      mastrDataGetter: keyof MastrPvPlantWithLocationDto | ((data: MastrPvPlantWithLocationDto) => unknown);
      allowedDeviation?: number;
      warnOnly?: boolean;
      show?: (
        model: GridRegistrationModel | FeederContractModel,
        pvPlant: MastrPvPlantWithLocationDto
      ) => boolean;
    }
  | {
      label: string;
      formFieldName: string; // Um einen Errortext zu generieren, wenn die Daten nicht übereinstimmen
      grDataGetter: (data: GridRegistrationModel | FeederContractModel) => unknown;
      mastrDataGetter: keyof MastrPvPlantWithLocationDto | ((data: MastrPvPlantWithLocationDto) => unknown);
      allowedDeviation?: number;
      warnOnly?: boolean;
      show?: (
        model: GridRegistrationModel | FeederContractModel,
        pvPlant: MastrPvPlantWithLocationDto
      ) => boolean;
    };
